/**
 * EventMsgV3 Byte Stuffing Implementation
 *
 * Implements the exact byte stuffing algorithm from the C implementation
 * to ensure protocol compatibility.
 */

/**
 * Control characters used in EventMsgV3 protocol
 */
export const CONTROL_CHARS = {
  SOH: 0x01, // Start of Header
  STX: 0x02, // Start of Text
  EOT: 0x04, // End of Transmission
  US: 0x1f, // Unit Separator
  ESC: 0x1b, // Escape Character
} as const;

/**
 * XOR mask for byte stuffing
 */
const STUFF_MASK = 0x20;

/**
 * Check if a byte is a control character that needs stuffing
 */
function isControlChar(byte: number): boolean {
  return (
    byte === CONTROL_CHARS.SOH ||
    byte === CONTROL_CHARS.STX ||
    byte === CONTROL_CHARS.EOT ||
    byte === CONTROL_CHARS.US ||
    byte === CONTROL_CHARS.ESC
  );
}

/**
 * Stuff (encode) bytes by escaping control characters
 *
 * Algorithm:
 * - If byte is a control character: Insert ESC, then XOR byte with 0x20
 * - Otherwise: Insert byte as-is
 *
 * @param data Input data to stuff
 * @returns Stuffed data
 */
export function stuff(data: Uint8Array): Uint8Array {
  const result: number[] = [];

  for (let i = 0; i < data.length; i++) {
    const byte = data[i];
    if (byte === undefined) {
      continue;
    }

    if (isControlChar(byte)) {
      // Escape the control character
      result.push(CONTROL_CHARS.ESC);
      result.push(byte ^ STUFF_MASK);
    } else {
      // Normal byte, no stuffing needed
      result.push(byte);
    }
  }

  return new Uint8Array(result);
}

/**
 * Unstuff (decode) bytes by handling escaped characters
 *
 * Algorithm:
 * - If byte is ESC: Mark next byte as escaped, continue
 * - If previous byte was ESC: XOR current byte with 0x20, add to output
 * - Otherwise: Add byte as-is to output
 *
 * @param data Stuffed data to unstuff
 * @returns Unstuffed data
 * @throws {Error} If data contains invalid escape sequences
 */
export function unstuff(data: Uint8Array): Uint8Array {
  const result: number[] = [];
  let escaped = false;

  for (let i = 0; i < data.length; i++) {
    const byte = data[i];
    if (byte === undefined) {
      continue;
    }
    if (escaped) {
      // Previous byte was ESC, so this byte is escaped
      result.push(byte ^ STUFF_MASK);
      escaped = false;
    } else if (byte === CONTROL_CHARS.ESC) {
      // This is an escape character, next byte will be escaped
      escaped = true;
    } else {
      // Normal byte
      result.push(byte);
    }
  }

  // Check for incomplete escape sequence
  if (escaped) {
    throw new Error(
      "Invalid byte stuffing: data ends with incomplete escape sequence"
    );
  }

  return new Uint8Array(result);
}

/**
 * Calculate the maximum possible size after stuffing
 * In worst case, every byte could be a control character, doubling the size
 *
 * @param originalSize Original data size
 * @returns Maximum size after stuffing
 */
export function getMaxStuffedSize(originalSize: number): number {
  return originalSize * 2; // Worst case: every byte is stuffed
}

/**
 * Calculate the minimum possible size after unstuffing
 * In best case, no bytes are stuffed
 *
 * @param stuffedSize Stuffed data size
 * @returns Minimum size after unstuffing
 */
export function getMinUnstuffedSize(stuffedSize: number): number {
  return Math.floor(stuffedSize / 2); // Worst case: every pair is stuffed
}

/**
 * Test if data contains any control characters that would need stuffing
 *
 * @param data Data to test
 * @returns True if data contains control characters
 */
export function needsStuffing(data: Uint8Array): boolean {
  for (let i = 0; i < data.length; i++) {
    const byte = data[i];

    if (byte !== undefined && isControlChar(byte)) {
      return true;
    }
  }
  return false;
}

/**
 * Validate that stuffed data has proper escape sequences
 *
 * @param data Stuffed data to validate
 * @returns True if data has valid stuffing
 */
export function isValidStuffing(data: Uint8Array): boolean {
  let escaped = false;

  for (let i = 0; i < data.length; i++) {
    const byte = data[i];
    if (byte === undefined) {
      continue;
    }

    if (escaped) {
      // Previous byte was ESC, this byte should be valid escaped value
      const unescaped = byte ^ STUFF_MASK;
      if (!isControlChar(unescaped)) {
        return false; // Invalid escape sequence
      }
      escaped = false;
    } else if (byte === CONTROL_CHARS.ESC) {
      escaped = true;
    } else if (isControlChar(byte)) {
      return false; // Unescaped control character
    }
  }

  return !escaped; // Should not end with incomplete escape
}
